const browser = !(typeof window === 'undefined');
const default_lang = 'ar';

function to_title_case(str: string) {
	return str.toLowerCase().replace(/\b\w/g, (s) => s.toUpperCase());
}

function create_state() {
	let current_state = $state(browser ? localStorage.getItem('locale') || default_lang : 'ar') as 'ar' | 'en';

	return {
		get value() {
			return current_state;
		},
		set value(value) {
			current_state = value;
			localStorage.setItem('locale', value);
		},
		get dir() {
			return current_state === 'en' ? 'ltr' : 'rtl';
		},
		get direction() {
			return current_state === 'en' ? 'left' : 'right';
		},

		switch_language() {
			current_state = current_state === 'en' ? 'ar' : 'en';
		}
	};
}

export const locale_state = create_state();

type LocOptions = 'title' | 'snake';

// Split "a|b|c" → ["a", "b", "c"]
type Split<S extends string, D extends string> = S extends `${infer L}${D}${infer R}` ? [L, ...Split<R, D>] : [S];

// "driver,theads" → "driver"  (strip everything after first comma)
type BeforeComma<S extends string> = S extends `${infer Key},${string}` ? Key : S;

// Validate each segment (after comma-strip) is a key
type CheckAll<T extends string[]> = T extends [infer H extends string, ...infer Rest extends string[]]
	? //           ^^^^^^^^^^^^^^^^^^^^^^^^
		BeforeComma<H> extends keyof typeof dictionary
		? CheckAll<Rest>
		: never
	: unknown;

type LocParam<S extends string> = CheckAll<Split<S, '|'>> extends never ? never : S;

export function Loc(word?: string, options?: LocOptions) {
	return translate_all(word, options);
}

export function LocDynamic(word?: string, options?: LocOptions) {
	return translate_all(word, options);
}

function translate_all(word?: string, options?: LocOptions) {
	if (!word || typeof word !== 'string') return '';
	if (word.includes('|'))
		return word
			.split('|')
			.map((word) => translate(word, options))
			.join(' ');
	else return translate(word, options);
}

function translate(word: string, options?: LocOptions) {
	if (!word) return '';
	word = word.toLowerCase();

	let add_the = false;
	const lang = locale_state.value;
	const check_the_operator = word.split(',');
	if (check_the_operator.length > 1) {
		word = check_the_operator[0];
		add_the = true;
	}

	const dictionary_word = dictionary[word];
	if (!dictionary_word) {
		return options === 'title' ? to_title_case(word) : word;
	}
	let translation = dictionary_word[lang];
	if (add_the && lang === 'ar' && translation) translation = `${'ال'}${translation}`;
	if (!translation) return options === 'title' ? to_title_case(word) : word;

	if (options === 'title') translation = to_title_case(translation);

	return translation ?? word;
}

// type Dictionary = {
// 	[key: string]: {
// 		[lang: string]: string;
// 	};
// };

const dictionary = {
	abbas: {
		ar: 'عباس ابن فرناس'
	},
	company: {
		ar: 'الشركة'
	},
	driver: {
		ar: 'سائق'
	},
	monthly_income: {
		ar: 'الدخل الشهري'
	},
	yes: {
		ar: 'نعم'
	},
	no: {
		ar: 'كلا'
	},
	status: {
		ar: 'حالة'
	},
	active: {
		ar: 'قيد العمل'
	},
	expired: {
		ar: 'مغلق'
	},
	not_rated_yet: {
		ar: 'لم يتم التقييم بعد'
	},
	rated: {
		ar: 'تم التقييم'
	},
	the_rating: {
		ar: 'التقييم'
	},
	buyer_location: {
		ar: 'موقع الزبون'
	},
	seller_location: {
		ar: 'موقع المتجر'
	},
	to_seller: {
		ar: 'إلى المتجر'
	},
	to_buyer: {
		ar: 'إلى الزبون'
	},
	delivery_price: {
		ar: 'قيمة التوصيل'
	},
	since: {
		ar: 'منذ'
	},
	month_start: {
		ar: 'بداية الشهر'
	},

	minute: {
		ar: 'دقيقة',
		en: 'minute'
	},
	minutes: {
		ar: 'دقائق'
	},

	minute_letter: {
		ar: 'د',
		en: 'm'
	},
	second_letter: {
		ar: 'ث',
		en: 's'
	},
	hour_letter: {
		ar: 'س',
		en: 'h'
	},
	m: {
		ar: 'د',
		en: 'm'
	},
	min: {
		ar: 'د',
		en: 'm'
	},
	s: {
		ar: 'ث',
		en: 's'
	},
	h: {
		ar: 'س',
		en: 'h'
	},

	second: {
		ar: 'ثانية'
	},
	seconds: {
		ar: 'ثوان'
	},
	the_hour: {
		ar: 'الساعة',
		en: 'hour'
	},
	km: {
		ar: 'كم'
	},
	and: {
		ar: 'و'
	},
	seller: {
		ar: 'متجر'
	},
	buyer: {
		ar: 'زبون'
	},
	total: {
		ar: 'إجمالي'
	},
	products: {
		ar: 'منتجات'
	},
	product: {
		ar: 'منتج'
	},
	first: {
		ar: 'البداية'
	},
	next: {
		ar: 'التالي'
	},
	previous: {
		ar: 'السابق'
	},
	complete: {
		ar: 'الانتهاء'
	},
	last: {
		ar: 'النهاية'
	},
	to: {
		ar: 'إلى'
	},
	showing: {
		ar: 'عرض'
	},
	welcome: {
		ar: 'مرحباً'
	},
	waiting_for_seller: {
		ar: 'بانتظار القبول'
	},
	seller_accepted: {
		ar: 'تم قبول الطلب'
	},
	waiting_for_driver_acceptance: {
		ar: 'جاري البحث عن سائق'
	},
	driver_assigned: {
		ar: 'تم تحديد سائق'
	},
	driver_arrived_seller: {
		ar: 'وصل السائق'
	},
	driver_to_buyer: {
		ar: 'السائق في طريقه للزبون'
	},
	driver_arrived_buyer: {
		ar: 'السائق وصل لموقع الزبون'
	},
	delivered: {
		ar: 'تم التوصيل'
	},
	cancelled: {
		ar: 'ملغي'
	},
	accept: {
		ar: 'قبول'
	},
	total_products: {
		ar: 'إجمالي المنتجات'
	},
	total_products_cost: {
		ar: 'إجمالي التكلفة'
	},
	new: {
		ar: 'جديدة'
	},
	inactive: {
		ar: 'منجزة'
	},
	login: {
		ar: 'تسجيل الدخول'
	},
	login_social_media: {
		ar: 'تسجيل دخول بحساب تواصل اجتماعي'
	},
	email: {
		ar: 'البريد الاكتروني'
	},
	password: {
		ar: 'كلمة المرور'
	},
	forgot_passowrd: {
		ar: 'نسيت كلمة المرور'
	},
	sign_in_google: {
		ar: 'تسجيل الدخول بحساب قووقل'
	},
	logout: {
		ar: 'تسجيل الخروج'
	},
	product_update_status_success: {
		ar: 'تم تحديث حالة المنتج بنجاح'
	},
	product_update_success: {
		ar: 'تم تحديث المنتج بنجاح'
	},
	edit: {
		ar: 'تعديل'
	},

	address: {
		ar: 'عنوان',
		en: 'address'
	},

	success: {
		ar: 'نجاح',
		en: 'success'
	},
	fail: {
		ar: 'فشل',
		en: 'fail'
	},
	failed: {
		ar: 'فشل',
		en: 'failed'
	},

	update: {
		ar: 'تحديث',
		en: 'update'
	},
	close: {
		ar: 'اغلاق',
		en: 'close'
	},
	closing_in: {
		ar: 'سيتم الاغلاق بعد',
		en: 'closing in'
	},
	edit_product: {
		ar: 'تعديل المنتج'
	},
	description: {
		ar: 'وصف'
	},
	price: {
		ar: 'سعر'
	},
	location: {
		ar: 'موقع'
	},
	harisa: {
		ar: 'هريسة'
	},
	bbq: {
		ar: 'شواء'
	},
	mayonnaiyse: {
		ar: 'مايونيز'
	},
	ketchup: {
		ar: 'كاتشب'
	},
	veggie: {
		ar: 'نباتي'
	},
	cheese: {
		ar: 'جبنة'
	},
	no_sauces: {
		ar: 'بدون سوسات'
	},
	last_updated: {
		ar: 'آخر تحديث'
	},
	not_synced: {
		ar: 'غير متصل'
	},
	undefined: {
		ar: 'غير محدد'
	},
	arrived_seller: {
		ar: 'وصلت إلى المتجر'
	},
	arrived_customer: {
		ar: 'وصلت إلى الزبون'
	},
	good_job: {
		ar: 'تمت العملية بنجاح'
	},
	ready: {
		ar: 'جاهز'
	},
	lyd: {
		ar: 'د.ل.'
	},
	remaining: {
		ar: 'متبقي'
	},
	location_description: {
		ar: 'وصف الموقع'
	},
	no_location_permission: {
		ar: 'التطبيق لا يملك صلاحية الوصول الى موقعك'
	},
	location_services_are_off: {
		ar: 'خدمات تحديد الموقع في هاتفك متوقفة. يرجى تفعيلها للاستمرار.'
	},
	open_settings: {
		ar: 'هل ترغب بفتح الاعدادات؟'
	},
	question: {
		ar: '؟'
	},
	available: {
		ar: 'متاح'
	},
	name: {
		ar: 'اسم'
	},
	unavailable: {
		ar: 'مقفل'
	},
	new_updates: {
		ar: 'تحديث جديد'
	},
	am: {
		ar: 'صباحاً'
	},
	pm: {
		ar: 'مساءً'
	},
	app_name: {
		ar: 'سائق شقّاقة'
	},
	app_brand: {
		ar: 'شقّاقة'
	},
	phone_number: {
		ar: 'رقم الهاتف'
	},
	password_provided_by_shaggaga: {
		ar: 'كلمة مرور حسابك بشقّاقة'
	},
	current: {
		ar: 'حالية'
	},
	sign_in: {
		ar: 'دخول'
	},
	sign_out: {
		ar: 'تسجيل الخروج'
	},
	messages: {
		ar: 'مراسلات'
	},
	balance: {
		ar: 'رصيد'
	},
	earnings: {
		ar: 'أرباح'
	},
	press_to_cancel: {
		ar: 'إضغط للإلغاء'
	},
	tracking_your: {
		ar: 'التطبيق يقوم بتسجيل موقع الهاتف'
	},
	will_request_mic_permission: {
		ar: 'يحتاج التطبيق الى هذه الصلاحية لتتمكن من التواصل مع فريق العمليات عبر الرسائل الصوتية'
	},
	will_request_push_permission: {
		ar: 'يحتاج التطبيق الى هذه الصلاحية لتتمكن من استقبال التحديثات الجديدة حين توفرها'
	},
	will_request_location_permission: {
		ar: 'يحتاج التطبيق الى هذه الصلاحية عند بدء العمل لتتمكن من ارسال موقعك الجعرافي للمتاجر والزبائن'
	},
	this_permission_is_optional: {
		ar: 'هذه الصلاحية اختيارية',
		en: 'this permission is optional'
	},
	this_permission_is_required: {
		ar: 'هذه الصلاحية اجبارية',
		en: 'this permission is required'
	},

	push_notifications_permissions: {
		ar: 'صلاحية دفع الإشعارات'
	},
	location_permission: {
		ar: 'صلاحية الموقع الجغرافي'
	},
	failed_to_obtain_location_permission: {
		ar: 'فشل الحصول على صلاحية الموقع الجغرافي',
		en: 'failed to obtain location permission'
	},
	failed_to_obtain_push_notifications_permission: {
		ar: 'فشل الحصول على صلاحية دفع الإشعارات',
		en: 'failed to obtain push notifications permission'
	},
	microphone_permissions: {
		ar: 'صلاحية المايكروفون'
	},
	try_again: {
		ar: 'محاولة أخرى'
	},
	attempt_failed: {
		ar: 'فشل المحاولة'
	},
	login_success: {
		ar: 'نجاح تسجيل الدخول'
	},

	login_failed: {
		ar: 'فشل تسجيل الدخول'
	},

	operation_success: {
		ar: 'تمت العملية بنجاح'
	},
	should_be_available_to_view: {
		ar: 'يجب أن تكون متاح لاستقبال الطلبات'
	},
	should_be_available_to_accept_route: {
		ar: 'يجب أن تكون متاح لقبول الخط'
	},
	should_be_available_to_update_shipment: {
		ar: 'يجب أن تكون متاح لتغيير حالة الشحنة'
	},
	location_not_able_setting_isavailable_to_off: {
		ar: 'لم نتكمن من التقاط موقعك، الرجاء الاعادة'
	},
	location_capturing_success: {
		ar: 'تم بدأ التقاط الموقع بنجاح'
	},
	network_status_off: {
		ar: 'لا يمكن الاتصال بالانترنت'
	},
	set_online_success: {
		ar: 'تم تغيير الحالة الى متاح'
	},
	set_offline_success: {
		ar: 'تم تغيير الحالة الى غير متاح'
	},
	status_change_success: {
		ar: 'تم تحديث '
	},
	status_change_failed: {
		ar: 'خلل خلال تخديث حالة الطلب'
	},
	error_pulling_data_from_server: {
		ar: 'خلل بتوثيق حسابك مع الخادم'
	},
	login_credintials_wrong: {
		ar: 'بيانات الدخول غير صحيحة'
	},
	not_able_to_get_geolocation: {
		ar: 'لم نحصل على موقعك'
	},
	open_app: {
		ar: 'الرجاء فتح التطبيق'
	},
	pulling_details_from_server: {
		ar: 'جاري تحميل بيانات الحساب'
	},
	request_permission: {
		ar: 'طلب الصلاحية'
	},
	redirect_to_onboarding: {
		ar: 'سيتم نقلك لصفحة الصلاحيات'
	},
	waiting_for_location: {
		ar: 'بانتظار التقاط الموقع الجغرافي'
	},
	waiting_for_location_dialog1: {
		ar: 'سيتم انهاء العملية في حالة'
	},
	waiting_for_location_dialog2: {
		ar: 'عدم التقاط الموقع بعد'
	},
	shipments: {
		ar: 'شحنات'
	},
	the: {
		ar: 'ال'
	},
	shipment: {
		ar: 'شحنة'
	},
	products_total: {
		ar: 'إجمالي المنتجات'
	},
	road_distance: {
		ar: 'المسافة على الطريق'
	},
	driving_time: {
		ar: 'الوقت على الطريق'
	},
	service_time: {
		ar: 'فترة الخدمة المتوقعة'
	},
	expected_total_duty_minutes: {
		ar: 'فترة العمل المتوقعة'
	},
	work_period: {
		ar: 'العمل'
	},
	route: {
		ar: 'خط',
		en: 'route'
	},
	routes: {
		ar: 'خطوط',
		en: 'routes'
	},
	shipping: {
		ar: 'شحن',
		en: 'shipping'
	},
	finished: {
		ar: 'منتهي',
		en: 'finished'
	},

	active_f: {
		ar: 'نشطة',
		en: 'active'
	},
	finished_f: {
		ar: 'منتهية',
		en: 'finished'
	},
	finished_route: {
		ar: 'خط شحن منتهي'
	},
	finished_routes: {
		ar: 'خطوط شحن منتهية'
	},
	route_statistics: {
		ar: 'احصائيات خط الشحن'
	},
	shipment_route: {
		ar: 'الخط الكلي للشحنات'
	},
	shipment_products: {
		ar: 'الشحنات'
	},
	sauces: {
		ar: 'سوسات'
	},
	extras: {
		ar: 'اضافات'
	},
	pickup_location: {
		ar: 'موقع المتجر'
	},
	pickup_shipments: {
		ar: 'شحنات المتجر'
	},
	shipments_length: {
		ar: 'عدد الشحنات'
	},
	pickup: {
		ar: 'استلام',
		en: 'pickup'
	},
	picked_up: {
		ar: 'تم الاستلام',
		en: 'picked up'
	},
	start: {
		ar: 'بداية',
		en: 'start'
	},
	end: {
		ar: 'نهاية',
		en: 'end'
	},
	delivery: {
		ar: 'توصيل',
		en: 'delivery'
	},

	buyer_phone_number: {
		ar: 'هاتف الزبون'
	},
	seller_phone_number: {
		ar: 'هاتف المتجر'
	},
	seller_name: {
		ar: 'اسم المتجر'
	},
	shipment_delivered: {
		ar: 'تم التوصيل'
	},

	shipment_picked_up: {
		ar: 'استلام الشحنة'
	},

	shipments_togo: {
		ar: 'شحنة متبقية'
	},

	customer_support: {
		ar: 'الدعم'
	},
	light_switch: {
		ar: 'الاضاءة'
	},
	add_address: {
		ar: 'اضافة عنوان'
	},
	confirm_delete: {
		ar: 'تأكيد عملية الحذف؟'
	},
	message: {
		ar: 'الرسالة'
	},

	confirm_send: {
		ar: 'تأكيد الإرسال'
	},

	confirm_audio: {
		ar: 'مراجعة التسجيل'
	},
	confirm_image: {
		ar: 'مراجعة الصورة'
	},
	send_location: {
		ar: 'ارسال موقع'
	},
	error_close_app: {
		ar: 'حدث خلل في التطبيق... الرجاء اغلاق التطبيق واعادة الفتح. رجاءاً قم باعلام فريق العمليات بالخلل ليتم اصلاحه'
	},
	messaging: {
		ar: 'المراسلات'
	},
	chat: {
		ar: 'التواصل'
	},
	chat_with_driver: {
		ar: 'تواصل مع السائق'
	},
	chat_with_seller: {
		ar: 'تواصل مع المتجر'
	},
	chat_with_buyer: {
		ar: 'تواصل مع الزبون'
	},
	profile_details: {
		ar: 'بيانات الحساب'
	},
	profile: {
		ar: 'حساب'
	},
	shipment_number: {
		ar: 'رقم الشحنة'
	},
	new_message: {
		ar: 'رسالة جديدة'
	},
	new_message_received: {
		ar: 'لديك رسالة جديدة'
	},
	new_notification_received: {
		ar: 'لديك اشعار جديد'
	},
	new_notification: {
		ar: 'اشعار جديد'
	},
	delivery_step: {
		ar: 'توصيل شحنات'
	},
	finish_route: {
		ar: 'انهاء خط الشحن'
	},

	finish_route_failed: {
		ar: 'فشل انهاء خط الشحن'
	},

	finish_route_success: {
		ar: 'نجاح انهاء خط الشحن'
	},

	accept_route: {
		ar: 'قبول خط الشحن'
	},
	accepting_route_failed: {
		ar: 'فشل قبول خط الشحن'
	},

	accepting_route_success: {
		ar: 'نجاح قبول خط الشحن'
	},

	status_deactivated: {
		ar: 'تم تغيير حالتك الى غير متاح'
	},
	not_able_to_get_geolocation_long_time: {
		ar: 'لم نتمكن من الحصول على موقعك لفترة طويلة'
	},
	new_chat_message: {
		ar: 'لديك رسالة جديدة'
	},

	confirm_finish_route: {
		ar: 'تأكيد انهاء خط الشحن'
	},
	image_send_failed: {
		ar: 'فشل ارسال الصورة'
	},
	image_send_success: {
		ar: 'نجاح ارسال الصورة'
	},
	metres: {
		ar: 'متر'
	},
	'km/h': {
		ar: 'كم/الساعة'
	},
	device_unable_voice_record: {
		ar: 'الجهاز غير قادر على التسجيل الصوتي'
	},

	posted: {
		ar: 'استلام طلب التوصيل من المتجر'
	},
	scheduled: {
		ar: 'تم تحديد خط التوصيل'
	},
	reschedule: {
		ar: 'سيتم إعادة تحديد خط التوصيل'
	},
	cancel_reason: {
		ar: 'سبب الالغاء'
	},
	'?': {
		ar: '؟'
	},

	update_password: {
		ar: 'تعديل كلمة المرور'
	},
	current_password: {
		ar: 'كلمة المرور الحالية'
	},

	new_password: {
		ar: 'كلمة المرور الجديدة'
	},
	forgot_password: {
		ar: 'نسيت كلمة المرور'
	},
	forgot_password_dialogue: {
		ar: 'الرجاء اختيار الموقع الجغرافي ﻷي موقع من المواقع المسجلة بحسابك للتأكد من ملكية الحساب'
	},
	forgot_password_dialogue2: {
		ar: 'في حالة عدم تمكنك من اعادة ضبط كلمة المرور بإمكانك التواصل مع فريق الدعم الفني'
	},

	confirm_sign_out: {
		ar: 'تأكيد تسجيل الخروج'
	},
	sign_out_success: {
		ar: 'نجاح تسجيل الخروج'
	},
	sign_out_failed: {
		ar: 'فشل تسجيل الخروج'
	},

	operation_failed: {
		ar: 'فشل العملية'
	},
	operation_fail: {
		ar: 'فشل العملية'
	},
	update_password_failed: {
		ar: 'فشل تحديث كلمة المرور'
	},
	update_password_success: {
		ar: 'نجاح تحديث كلمة المرور'
	},

	amount: {
		ar: 'قيمة',
		en: 'amount'
	},

	partial: {
		ar: 'جزئي'
	},
	full: {
		ar: 'كامل'
	},
	extra: {
		ar: 'فائض'
	},
	remaining_amount: {
		ar: 'القيمة المتبقية'
	},
	extra_amount: {
		ar: 'القيمة الزائدة'
	},
	route_shipments: {
		ar: 'شحنات الخط'
	},
	monthly: {
		ar: 'شهري'
	},
	debit_balance: {
		ar: 'الرصيد المستحق'
	},
	monthly_earnings: {
		ar: 'الارباح الشهرية'
	},

	duration: {
		ar: 'مدة',
		en: 'duration'
	},
	step: {
		ar: 'خطوة',
		en: 'step'
	},
	steps: {
		ar: 'خطوات',
		en: 'steps'
	},
	view: {
		ar: 'عرض'
	},
	of: {
		ar: 'من',
		en: 'of'
	},
	quantity: {
		ar: 'كمية',
		en: 'quantity'
	},

	collection_amount: {
		ar: 'تحصيل',
		en: 'collection'
	},

	reason: {
		ar: 'سبب',
		en: 'reason'
	},

	edit_collection_amount: {
		ar: 'استلام قيمة مختلفة',
		en: 'edit collection amount'
	},

	profit: {
		ar: 'ربح',
		en: 'profit'
	},
	start_location: {
		ar: 'موقع البداية',
		en: 'start location'
	},
	end_location: {
		ar: 'موقع النهاية',
		en: 'end location'
	},
	destination: {
		ar: 'وجهة',
		en: 'destination'
	},
	number: {
		ar: 'رقم',
		en: 'number'
	},
	date_created: {
		ar: 'تاريخ الإنشاء',
		en: 'date created'
	},
	search: {
		ar: 'بحث',
		en: 'search'
	},
	profits: {
		ar: 'ارباح',
		en: 'profits'
	},
	day: {
		ar: 'يوم',
		en: 'day'
	},
	month: {
		ar: 'شهر',
		en: 'month'
	},
	year: {
		ar: 'سنة',
		en: 'year'
	},
	week: {
		ar: 'أسبوع',
		en: 'week'
	},
	days: {
		ar: 'أيام',
		en: 'days'
	},
	months: {
		ar: 'أشهر',
		en: 'months'
	},
	years: {
		ar: 'سنوات',
		en: 'years'
	},
	weeks: {
		ar: 'أسابيع',
		en: 'weeks'
	},
	hour: {
		ar: 'ساعة',
		en: 'hour'
	},
	hours: {
		ar: 'ساعات',
		en: 'hours'
	},
	hours_short: {
		ar: 'س',
		en: 'h'
	},
	distance: {
		ar: 'مسافة',
		en: 'distance'
	},
	map: {
		ar: 'خريطة',
		en: 'map'
	},
	maps: {
		ar: 'خرائط',
		en: 'maps'
	},
	remaining_f: {
		ar: 'متبقية',
		en: 'remaining'
	},

	invoice: {
		ar: 'فاتورة',
		en: 'invoice'
	},
	invoices: {
		ar: 'فواتير',
		en: 'invoices'
	},
	collection: {
		ar: 'تحصيل',
		en: 'collection'
	},
	collections: {
		ar: 'تحصيلات',
		en: 'collections'
	},
	payouts: {
		ar: 'مدفوعات',
		en: 'payouts'
	},
	count: {
		ar: 'عدد',
		en: 'count'
	},
	shaggaga_address: {
		ar: 'طرابلس، ليبيا',
		en: 'Tripoli, Libya'
	},
	date: {
		ar: 'تاريخ',
		en: 'date'
	},
	payment: {
		ar: 'دفع',
		en: 'payment'
	},
	payment_date: {
		ar: 'تاريخ الدفع',
		en: 'payment date'
	},
	issuance_date: {
		ar: 'تاريخ الاصدار',
		en: 'issuance date'
	},
	unpaid: {
		ar: 'غير مدفوع',
		en: 'unpaid'
	},
	paid: {
		ar: 'مدفوع',
		en: 'paid'
	},
	payment_status: {
		ar: 'حالة الدفع',
		en: 'payment status'
	},
	issue: {
		ar: 'اصدار',
		en: 'issue'
	},

	total_revenue: {
		ar: 'إجمالي الإيرادات',
		en: 'total revenue'
	},
	current_balance: {
		ar: 'الرصيد الحالي',
		en: 'current balance'
	},
	monthly_revenue: {
		ar: 'الإيرادات الشهرية',
		en: 'monthly revenue'
	},
	numbers: {
		ar: 'أرقام',
		en: 'numbers'
	},
	delivery_date: {
		ar: 'تاريخ التسليم',
		en: 'delivery date'
	},
	cost: {
		ar: 'تكلفة',
		en: 'cost'
	},
	type: {
		ar: 'نوع',
		en: 'type'
	},

	enable: {
		ar: 'تفعيل',
		en: 'enable'
	},

	save: {
		ar: 'حفظ',
		en: 'save'
	},
	print: {
		ar: 'طباعة',
		en: 'print'
	},
	address_description: {
		ar: 'وصف الموقع'
	},
	download: {
		ar: 'تحميل'
	},
	qrcode: {
		ar: 'رمز التعقب',
		en: 'qrcode'
	},

	share: {
		ar: 'مشاركة'
	},
	shipment_qrcode: {
		ar: 'رمز تعقب الشحنة'
	},
	copy_url: {
		ar: 'نسخ الرابط'
	},
	no_options_found: {
		ar: 'لم يتم العثور على نتائج',
		en: 'no options found'
	},
	update_availibility: {
		ar: 'تحديث الحالة',
		en: 'update availability'
	},
	location_capture: {
		ar: 'التقاط الموقع',
		en: 'location capture'
	},
	select: {
		ar: 'اختر',
		en: 'select'
	},

	box: {
		ar: 'صندوق',
		en: 'box'
	},
	boxes: {
		ar: 'صناديق',
		en: 'boxes'
	},

	drag_drop1: {
		ar: 'اسحب الصورة هنا'
	},
	drag_drop2: {
		ar: 'او انقر للتحديد '
	},
	send_message: {
		ar: 'اكتب رسالة ...',
		en: 'Type a message...'
	},

	standard_size_s: {
		ar: 'صغير',
		en: 'S'
	},
	standard_size_m: {
		ar: 'متوسط',
		en: 'M'
	},
	standard_size_l: {
		ar: 'كبير 1',
		en: 'L'
	},
	standard_size_xl: {
		ar: 'كبير 2',
		en: 'XL'
	},
	standard_size_xxl: {
		ar: 'كبير 3',
		en: 'XXL'
	},

	width: {
		ar: 'عرض',
		en: 'width'
	},
	height: {
		ar: 'ارتفاع',
		en: 'height'
	},
	length: {
		ar: 'طول',
		en: 'length'
	},

	dimensions: {
		ar: 'مقاسات',
		en: 'dimensions'
	},
	standard_size: {
		ar: 'حجم عام',
		en: 'standard size'
	},
	cm: {
		ar: 'سم',
		en: 'cm'
	},

	phone: {
		ar: 'هاتف',
		en: 'phone'
	},
	cancel_note_required: {
		ar: 'الرجاء ادخال ملاحظات الالغاء',
		en: 'please enter a concellation note'
	},
	seller_main_address: {
		ar: 'موقع المتجر الرئيسي',
		en: 'seller main address'
	},
	notes: {
		ar: 'ملاحظات',
		en: 'notes'
	},
	note: {
		ar: 'ملاحظة',
		en: 'note'
	},
	eta: {
		ar: 'وقت التوصيل',
		en: 'ETA'
	},

	url: {
		ar: 'رابط',
		en: 'url'
	},
	coordinates: {
		ar: 'احداثيات',
		en: 'coordinates'
	},
	extract: {
		ar: 'استخراج',
		en: 'extract'
	},
	extract_coordinates_failed: {
		ar: 'فشل استخراج احداثيات الموقع',
		en: 'failed to extract coordinates'
	},
	invalid_coordinates: {
		ar: 'الاحداثيات غير صحيحة',
		en: 'invalid coordinates'
	},
	coordinates_set: {
		ar: 'تم تحديد الاحداثيات',
		en: 'coordinates set'
	},
	clipboard: {
		ar: 'حافظة',
		en: 'clipboard'
	},
	clipboard_empty: {
		ar: 'الحافظة فارغة',
		en: 'clipboard empty'
	},
	copy_from_clipboard: {
		ar: 'نسخ من الحافظة',
		en: 'copy from clipboard'
	},
	extract_location_from_url: {
		ar: 'استخراج الموقع من الرابط',
		en: 'extract location from url'
	},
	permission: {
		ar: 'صلاحية',
		en: 'permission'
	},
	permissions: {
		ar: 'صلاحيات',
		en: 'permissions'
	},
	no_specific_dates: {
		ar: 'لا توجد تواريخ محددة مضافة',
		en: 'no specific dates added'
	},

	weekly_windows_description: {
		ar: 'حدد فترات محددة لأي يوم من أيام الاسبوع',
		en: 'Define specific windows for each day of the week.These rules will override the global rules on their respective days.'
	},
	image: {
		ar: 'صورة'
	},

	upload_image: {
		ar: 'رفع صورة',
		en: 'upload image'
	},

	exists: {
		ar: 'موجود',
		en: 'exists'
	},
	global_f: {
		ar: 'عامة',
		en: 'global'
	},
	weekly_f: {
		ar: 'اسبوعية',
		en: 'weekly'
	},

	window: {
		ar: 'فترة',
		en: 'window'
	},
	windows: {
		ar: 'فترات',
		en: 'windows'
	},
	friday: {
		ar: 'الجمعة',
		en: 'friday'
	},
	saturday: {
		ar: 'السبت',
		en: 'saturday'
	},

	sunday: {
		ar: 'الأحد',
		en: 'sunday'
	},
	monday: {
		ar: 'الاثنين',
		en: 'monday'
	},
	tuesday: {
		ar: 'الثلاثاء',
		en: 'tuesday'
	},
	wednesday: {
		ar: 'الأربعاء',
		en: 'wednesday'
	},
	thursday: {
		ar: 'الخميس',
		en: 'thursday'
	},

	weekly_windows_summary: {
		ar: 'تحديد فترات اسبوعية',
		en: 'select weekly windows'
	},
	added: {
		ar: 'مضافة',
		en: 'added'
	},
	time_range_exists: {
		ar: 'نفس الفترة موجودة',
		en: 'this time window already exists'
	},

	duty_windows: {
		ar: 'فترات العمل',
		en: 'duty_windows'
	},
	today: {
		ar: 'اليوم',
		en: 'today'
	},
	all_days: {
		ar: 'كل الأيام',
		en: 'all days'
	},
	like_usual: {
		ar: 'مثل المعتاد',
		en: 'like usual'
	},
	special_hours: {
		ar: 'ساعات خاصة',
		en: 'special hours'
	},
	vacation: {
		ar: 'إجازة',
		en: 'day off'
	},
	every_week: {
		ar: 'كل أسبوع',
		en: 'every week'
	},
	this_week_only: {
		ar: 'هذا الأسبوع فقط',
		en: 'this week only'
	},
	temp: {
		ar: 'مؤقت',
		en: 'temp'
	},
	until: {
		ar: 'حتى',
		en: 'to'
	},
	increase: {
		ar: 'زيادة',
		en: 'increase'
	},
	decrease: {
		ar: 'إنقاص',
		en: 'decrease'
	},
	dates: {
		ar: 'تواريخ',
		en: 'dates'
	},
	specific_dates: {
		ar: 'تواريخ محددة',
		en: 'specific_dates'
	},

	paid_by: {
		ar: 'دفع من طرف',
		en: 'paid by'
	},
	pending_online_payment: {
		ar: 'بانتظار الدفع الالكتروني',
		en: 'pending online payment'
	},
	add: {
		ar: 'اضافة',
		en: 'add'
	},
	included_window: {
		ar: 'فترة مفضلة',
		en: 'included window'
	},
	included_windows: {
		ar: 'فترات مفضلة',
		en: 'included windows'
	},
	excluded_window: {
		ar: 'فترة مستثناة',
		en: 'excluded window'
	},
	excluded_windows: {
		ar: 'فترات مستثناة',
		en: 'excluded windows'
	},

	included_windows_the: {
		ar: 'الفترات المفضلة',
		en: 'included windows'
	},

	excluded_windows_the: {
		ar: 'الفترات المستثناة',
		en: 'excluded windows'
	},

	preferred_delivery_windows: {
		ar: 'فترات التوصيل المفضلة',
		en: 'preferred delivery times'
	},
	pickup_windows: {
		ar: 'فترات الاستلام',
		en: 'pickup times'
	},

	include_has_similar: {
		ar: 'الفترة مطابقة لفترة من الفترات المفضلة',
		en: 'include windows has an identical window'
	},
	exclude_has_similar: {
		ar: 'الفترة مطابقة لفترة من الفترات المستثناة',
		en: 'exclude windows has an identical window'
	},

	tripoli: {
		ar: 'طرابلس',
		en: 'tripoli'
	},
	zawiyah: {
		ar: 'الزاوية',
		en: 'zawiyah'
	},
	misurata: {
		ar: 'مصراتة',
		en: 'misurata'
	},
	benghazi: {
		ar: 'بنغازي',
		en: 'benghazi'
	},
	sabha: {
		ar: 'سبها',
		en: 'sabha'
	},

	key_metrics: {
		ar: 'المقاييس الرئيسية',
		en: 'key metrics'
	},
	from_last_month: {
		ar: 'من الشهر الماضي',
		en: 'from last month'
	},
	not_available: {
		ar: 'لا يوجد',
		en: 'not available'
	},

	am_short: {
		ar: 'ص',
		en: 'am'
	},
	pm_short: {
		ar: 'م',
		en: 'pm'
	},
	remove: {
		ar: 'حذف',
		en: 'remove'
	},
	time_range_conflict: {
		ar: 'هذه الفترة تتعارض مع فترة أخرى موجودة',
		en: 'this time conflicts with an existing time range'
	},
	rest_of_route: {
		ar: 'بقية الخط',
		en: 'rest of route'
	},
	required: {
		ar: 'مطلوب',
		en: 'required'
	},
	optional: {
		ar: 'اختياري',
		en: 'optional'
	},
	navigate_remaining_steps: {
		ar: 'التوجه لبقية الخطوات',
		en: 'navigate remaining steps'
	},

	navigate_to_location: {
		ar: 'التوجه للموقع',
		en: 'navigate to location'
	},
	stops: {
		ar: 'محطات',
		en: 'stops'
	},

	// Step Status
	delayed: {
		ar: 'متأخر',
		en: 'delayed'
	},
	completed: {
		ar: 'مكتمل',
		en: 'completed'
	},
	pending: {
		ar: 'قيد الانتظار',
		en: 'pending'
	},

	call: {
		ar: 'اتصال',
		en: 'call'
	},
	whatsapp: {
		ar: 'واتساب',
		en: 'whatsapp'
	},
	arrived: {
		ar: 'وصلت',
		en: 'arrived'
	},
	delay: {
		ar: 'تأخير',
		en: 'delay'
	},
	support: {
		ar: 'الدعم',
		en: 'support'
	},

	// Delay Actions
	reactivate: {
		ar: 'إعادة تفعيل',
		en: 'reactivate'
	},

	// Route Progress
	route_progress: {
		ar: 'تقدم الخط',
		en: 'route progress'
	},
	delayed_steps: {
		ar: 'خطوات مؤجلة',
		en: 'delayed steps'
	},
	upcoming_steps: {
		ar: 'الخطوات القادمة',
		en: 'upcoming steps'
	},

	// Collection & Money
	collect: {
		ar: 'تحصيل',
		en: 'collect'
	},
	collected: {
		ar: 'تم تحصيله',
		en: 'collected'
	},
	collected_amount: {
		ar: 'المبلغ المحصل',
		en: 'collected amount'
	},
	due: {
		ar: 'المستحق',
		en: 'due'
	},

	// Shipments
	items: {
		ar: 'عناصر',
		en: 'items'
	},

	// Shipment Actions
	confirm_pickup: {
		ar: 'تأكيد الاستلام',
		en: 'confirm pickup'
	},
	noshow: {
		ar: 'لم يتواجد',
		en: 'no show'
	},
	mark_as_noshow: {
		ar: 'تسجيل كعدم تواجد',
		en: 'mark as no show'
	},
	rescheduled: {
		ar: 'تمت إعادة الجدولة',
		en: 'rescheduled'
	},
	reschedule_instead_cancel: {
		ar: 'إعادة جدولة بدلاً من الإلغاء',
		en: 'reschedule instead of cancel'
	},

	// Notes & Discrepancy
	reason_note: {
		ar: 'سبب/ملاحظة',
		en: 'reason/note'
	},
	explain_noshow: {
		ar: 'اشرح سبب عدم التواجد',
		en: 'explain the no show'
	},
	discrepancy_note_required: {
		ar: 'ملاحظة الفرق مطلوبة',
		en: 'discrepancy note required'
	},
	explain_difference: {
		ar: 'اشرح الفرق في المبلغ',
		en: 'explain the difference'
	},
	enter_discrepancy_note: {
		ar: 'أدخل ملاحظة للفرق',
		en: 'enter a note for the discrepancy'
	},

	// Contact
	contact_operations: {
		ar: 'الاتصال بالعمليات',
		en: 'contact operations'
	},
	eta_message: {
		ar: 'سأصل خلال {time}',
		en: 'I will arrive by {time}'
	},

	// UI Elements
	back: {
		ar: 'رجوع',
		en: 'back'
	},
	loading: {
		ar: 'جاري التحميل',
		en: 'loading'
	},
	unknown: {
		ar: 'غير معروف',
		en: 'unknown'
	},
	route_not_found: {
		ar: 'الخط غير موجود',
		en: 'route not found'
	},

	// Shipment Status
	shipment_actions: {
		ar: 'إجراءات الشحنة',
		en: 'shipment actions'
	},
	shipment_not_picked_up: {
		ar: 'الشحنة لم يتم استلامها بعد',
		en: 'shipment not picked up yet'
	},

	// Success Messages
	success_activate: {
		ar: 'تم التفعيل بنجاح',
		en: 'activated successfully'
	},
	success_delay: {
		ar: 'تم التأجيل بنجاح',
		en: 'delayed successfully'
	},
	success_reactivate: {
		ar: 'تمت إعادة التفعيل بنجاح',
		en: 'reactivated successfully'
	},
	success_pickup: {
		ar: 'تم الاستلام بنجاح',
		en: 'picked up successfully'
	},
	success_delivery: {
		ar: 'تم التوصيل بنجاح',
		en: 'delivered successfully'
	},
	success_cancel: {
		ar: 'تم الإلغاء بنجاح',
		en: 'cancelled successfully'
	},
	success_finish_route: {
		ar: 'تم إنهاء الخط بنجاح',
		en: 'route finished successfully'
	},

	// Failure Messages
	fail_activate: {
		ar: 'فشل في التفعيل',
		en: 'failed to activate'
	},
	fail_delay: {
		ar: 'فشل في التأجيل',
		en: 'failed to delay'
	},
	fail_reactivate: {
		ar: 'فشل في إعادة التفعيل',
		en: 'failed to reactivate'
	},
	fail_pickup: {
		ar: 'فشل في الاستلام',
		en: 'failed to pick up'
	},
	fail_delivery: {
		ar: 'فشل في التوصيل',
		en: 'failed to deliver'
	},
	fail_cancel: {
		ar: 'فشل في الإلغاء',
		en: 'failed to cancel'
	},
	fail_finish_route: {
		ar: 'فشل في إنهاء الخط',
		en: 'failed to finish route'
	},

	// Cost labels
	products_cost: {
		ar: 'تكلفة المنتجات',
		en: 'products cost'
	},
	shipping_cost: {
		ar: 'تكلفة الشحن',
		en: 'shipping cost'
	},
	free_shipping: {
		ar: 'شحن مجاني',
		en: 'Free shipping'
	},
	discount: {
		ar: 'خصم',
		en: 'Discount'
	},
	total_to_collect: {
		ar: 'المجموع للتحصيل',
		en: 'total to collect'
	},
	amount_paid: {
		ar: 'المبلغ المدفوع',
		en: 'amount paid'
	},
	cost_to_collect: {
		ar: 'المطلوب تحصيله',
		en: 'cost to collect'
	},

	address_description_placeholder: {
		ar: 'اسم المبنى، رقم الشقة، معلم بارز، إلخ.',
		en: 'Building name, apartment number, landmark, etc.'
	},
	amount_differs_from_required: {
		ar: 'المبلغ يختلف عن المطلوب',
		en: 'Amount differs from required'
	},
	arrived_at_depot: {
		ar: 'الوصول إلى المستودع',
		en: 'Arrived at depot'
	},
	begin_from_warehouse: {
		ar: 'ابدأ من المستودع',
		en: 'Begin from warehouse'
	},
	buyer_not_available: {
		ar: 'المشتري غير متاح؟',
		en: 'Buyer not available?'
	},
	cancel_shipment_hint: {
		ar: 'تعذّر الوصول للمشتري. ألغِ هذه الشحنة حتى لا تعيق المسار.',
		en: "Buyer not reachable. Cancel this shipment so it doesn't block the route."
	},
	cancel_shipment_noshow: {
		ar: 'إلغاء الشحنة (لم يتواجد)',
		en: 'Cancel shipment (no-show)'
	},
	cancel_which_shipment: {
		ar: 'إلغاء أي شحنة؟',
		en: 'Cancel which shipment?'
	},
	check_back_later: {
		ar: 'تحقق لاحقاً أو تواصل مع المُنسّق.',
		en: 'Check back later or contact your dispatcher.'
	},
	collapse_map: {
		ar: 'تصغير الخريطة',
		en: 'Collapse map'
	},
	confirm: {
		ar: 'تأكيد',
		en: 'Confirm'
	},
	confirm_collection_of: {
		ar: 'تأكيد تحصيل',
		en: 'Confirm collection of'
	},
	confirm_delivery: {
		ar: 'تأكيد التسليم',
		en: 'Confirm delivery'
	},
	delay_step: {
		ar: 'تأجيل الخطوة',
		en: 'Delay step'
	},
	delay_step_hint: {
		ar: 'سيتم استبعاد الخطوة من "ابدأ الملاحة للمسار". يمكنك إعادة تفعيلها من صف المؤجلة.',
		en: 'Step will be excluded from "Navigate the route". You can reactivate it from the Delayed chips row.'
	},
	deliver_all: {
		ar: 'تسليم الكل',
		en: 'Deliver all'
	},
	done: {
		ar: 'تم',
		en: 'Done'
	},
	edit_address: {
		ar: 'تعديل العنوان',
		en: 'Edit address'
	},
	edit_amount_hint: {
		ar: 'فعّل إذا دفع المشتري مبلغاً مختلفاً (بقشيش، دفع جزئي، إلخ)',
		en: 'Toggle on if buyer paid a different amount (tip, partial, etc.)'
	},
	edit_collected_amount: {
		ar: 'تعديل المبلغ المحصّل',
		en: 'Edit collected amount'
	},
	expand_map: {
		ar: 'تكبير الخريطة',
		en: 'Expand map'
	},
	item: {
		ar: 'عنصر',
		en: 'item'
	},
	left: {
		ar: 'متبقية',
		en: 'left'
	},
	loading_route: {
		ar: 'جاري تحميل المسار…',
		en: 'Loading your route…'
	},
	low_signal: {
		ar: 'إشارة ضعيفة',
		en: 'Low signal'
	},
	mark_delayed: {
		ar: 'وضع علامة مؤجل',
		en: 'Mark delayed'
	},
	mark_step_delayed: {
		ar: 'تأجيل الخطوة',
		en: 'Mark step as delayed'
	},
	mark_step_delayed_hint: {
		ar: 'المشتري غير متاح. تابع المسار وعُد لاحقاً. ستنتقل الخطوة إلى "المؤجلة" — يمكنك إعادة تفعيلها في أي وقت.',
		en: 'Buyer not available. Carry on with the route and come back later. The step moves to "Delayed" — you can reactivate it anytime.'
	},
	navigate_here_only: {
		ar: 'ملاحة إلى هنا فقط',
		en: 'Navigate here only'
	},
	navigate_the_route: {
		ar: 'ابدأ الملاحة للمسار',
		en: 'Navigate the route'
	},
	next_up: {
		ar: 'التالية',
		en: 'Next up'
	},
	no_active_route: {
		ar: 'لا يوجد مسار نشط مُسند.',
		en: 'No active route assigned.'
	},
	no_cancellable_shipments: {
		ar: 'لا توجد شحنات قابلة للإلغاء في هذه الخطوة.',
		en: 'No cancellable shipments on this step.'
	},
	no_map_data: {
		ar: 'لا توجد بيانات خريطة',
		en: 'No map data available'
	},
	no_route_shape: {
		ar: 'لا توجد بيانات شكل المسار',
		en: 'No route shape data'
	},
	no_stops_left: {
		ar: 'لا توجد محطات متبقية',
		en: 'No stops left'
	},
	no_upcoming_steps: {
		ar: 'لا توجد خطوات تالية.',
		en: 'No upcoming steps.'
	},
	note_optional: {
		ar: 'ملاحظة (اختياري)',
		en: 'Note (optional)'
	},
	note_placeholder: {
		ar: 'سبب الاختلاف (مثال: دفع المشتري نقداً، دفع جزئي)',
		en: 'Reason for discrepancy (e.g. buyer paid in cash, partial payment)'
	},
	note_required: {
		ar: 'ملاحظة مطلوبة',
		en: 'A note is required'
	},
	now: {
		ar: 'الآن',
		en: 'Now'
	},
	offline: {
		ar: 'غير متصل',
		en: 'Offline'
	},
	pickup_all: {
		ar: 'استلام الكل',
		en: 'Pick up all'
	},
	reschedule_hint: {
		ar: 'طلب المشتري التأجيل. ستتم إعادة جدولة الشحنة لمسار مستقبلي.',
		en: 'Buyer asked to delay. The shipment will be rescheduled for a future route.'
	},
	reschedule_which_shipment: {
		ar: 'إعادة جدولة أي شحنة؟',
		en: 'Reschedule which shipment?'
	},
	route_cancel_label: {
		ar: 'إلغاء الشحنة',
		en: 'Cancel shipment'
	},
	route_cannot_finish_incomplete: {
		ar: 'لا يمكن الإنهاء — هناك خطوات غير مكتملة بعد.',
		en: 'Cannot finish — some steps are not done yet.'
	},
	route_delay_label: {
		ar: 'تأجيل الخطوة',
		en: 'Delay step'
	},
	route_deliver_label: {
		ar: 'تسليم',
		en: 'Deliver'
	},
	route_deliver_queued: {
		ar: 'جاري مزامنة التسليم…',
		en: 'Delivery syncing…'
	},
	route_finish_label: {
		ar: 'إنهاء المسار',
		en: 'Finish route'
	},
	route_finishing_route: {
		ar: 'جاري إنهاء المسار…',
		en: 'Finishing route…'
	},
	route_navigate_truncated: {
		ar: 'المسار يحتوي على محطات كثيرة؛ سيتم فتح أول 10 في الخرائط. أعد الملاحة بعد المحطة 10.',
		en: 'Route has many stops; opening first 10 in maps. Re-navigate after stop 10.'
	},
	route_no_navigable_steps: {
		ar: 'لا توجد خطوات قابلة للملاحة. جميع الخطوات المتبقية مؤجلة أو مكتملة.',
		en: 'No navigable steps. All remaining steps are delayed or completed.'
	},
	route_no_step_coords: {
		ar: 'لا توجد إحداثيات لهذه الخطوة. تعذرت الملاحة.',
		en: 'This step has no coordinates. Cannot navigate.'
	},
	route_no_steps_to_start: {
		ar: 'لا توجد خطوات لبدء هذا المسار.',
		en: 'No steps to start on this route.'
	},
	route_pickup_label: {
		ar: 'استلام',
		en: 'Pickup'
	},
	route_pickup_queued: {
		ar: 'جاري مزامنة الاستلام…',
		en: 'Pickup syncing…'
	},
	route_reactivate_label: {
		ar: 'إعادة تفعيل الخطوة',
		en: 'Reactivate step'
	},
	route_reschedule_label: {
		ar: 'إعادة جدولة الشحنة',
		en: 'Reschedule shipment'
	},
	route_retrying_action: {
		ar: 'جاري إعادة محاولة الإجراء الأخير…',
		en: 'Retrying last action…'
	},
	route_send_invoice_label: {
		ar: 'إرسال الفاتورة',
		en: 'Send invoice'
	},
	route_sending_invoice: {
		ar: 'جاري إرسال الفاتورة عبر واتساب…',
		en: 'Sending invoice via WhatsApp…'
	},
	route_set_active_label: {
		ar: 'تعيين كخطوة حالية',
		en: 'Set step as current'
	},
	route_shipment_cancelled: {
		ar: 'تم إلغاء الشحنة (لم يتواجد المشتري).',
		en: 'Shipment cancelled (no-show).'
	},
	route_shipment_rescheduled: {
		ar: 'تمت إعادة جدولة الشحنة لمسار مستقبلي.',
		en: 'Shipment rescheduled for a future route.'
	},
	route_step_delayed: {
		ar: 'تم تأجيل الخطوة. تابع المسار.',
		en: 'Step marked as delayed. Carry on with the route.'
	},
	route_step_reactivated: {
		ar: 'تمت إعادة تفعيل الخطوة. أصبحت الآن ضمن "التالية".',
		en: 'Step reactivated. It is now in Next up.'
	},
	route_step_set_active: {
		ar: 'تم تعيين الخطوة كحالية. عادت الخطوة السابقة إلى "التالية".',
		en: 'Step set as current. Previous step moved back to Next up.'
	},
	save_address: {
		ar: 'حفظ العنوان',
		en: 'Save address'
	},
	saving_ellipsis: {
		ar: 'جاري الحفظ…',
		en: 'Saving…'
	},
	send_invoice_wa: {
		ar: 'إرسال الفاتورة (واتساب)',
		en: 'Send invoice (WhatsApp)'
	},
	set_as_current: {
		ar: 'تعيين كحالية',
		en: 'Set as current'
	},
	settings: {
		ar: 'الإعدادات',
		en: 'Settings'
	},
	start_route: {
		ar: 'ابدأ المسار',
		en: 'Start route'
	},
	stop: {
		ar: 'محطة',
		en: 'Stop'
	},
	syncing: {
		ar: 'مزامنة',
		en: 'syncing'
	},
	syncing_ellipsis: {
		ar: 'جاري المزامنة…',
		en: 'Syncing…'
	},
	tap_map_to_set_pin: {
		ar: 'اضغط على الخريطة لتحديد موقع جديد لهذا المشتري.',
		en: 'Tap on the map to set a new pin location for this buyer.'
	},
	to_collect: {
		ar: 'للتحصيل',
		en: 'to collect'
	},
	unknown_buyer: {
		ar: 'مشتري غير معروف',
		en: 'Unknown buyer'
	},
	unknown_seller: {
		ar: 'بائع غير معروف',
		en: 'Unknown seller'
	},
	update_delivery_address: {
		ar: 'تحديث عنوان التسليم',
		en: 'Update delivery address'
	},
	all_steps_complete: {
		ar: 'اكتملت جميع الخطوات',
		en: 'All steps complete'
	},
	no_active_step_tap_start: {
		ar: 'لا توجد خطوة نشطة. اضغط "ابدأ المسار" بالأسفل.',
		en: 'No active step. Tap "Start route" below to begin.'
	},
	confirm_deliver_all: {
		ar: 'أنت على وشك تسليم جميع الشحنات المعلقة في هذه الخطوة. تحقق من الإجماليات وأضف ملاحظة إذا لزم الأمر.',
		en: 'You are about to deliver all pending shipments on this step. Verify the totals and add a note if needed.'
	},
	view_full_route_map: {
		ar: 'View full route map',
		en: 'عرض خريطة المسار بالكامل'
	},
	items_details: {
		ar: 'تفاصيل العناصر',
		en: 'Items details'
	},
	reactivate_step: {
		ar: 'إعادة تفعيل الخطوة المؤجلة',
		en: 'Reactivate delayed step'
	},
	reactivate_step_hint: {
		ar: '',
		en: ''
	},
	go_there_now: {
		ar: 'اذهب الآن',
		en: 'Go there now'
	},
	go_there_now_hint: {
		ar: 'تعيين كخطوة حالية والملاحة فوراً.',
		en: 'Set as current step and navigate immediately.'
	},
	back_to_upcoming: {
		ar: 'العودة للقائمة',
		en: 'Back to upcoming'
	},
	back_to_upcoming_hint: {
		ar: 'إعادتها إلى ترتيبها الأصلي في الخطوات المعلقة.',
		en: 'Return to its original order in the pending list.'
	},
	navigate_here_hint: {
		ar: 'فتح الخريطة لهذا الموقع دون تغيير المسار.',
		en: 'Open map to this location without changing the route.'
	},
	cancel: {
		ar: 'إلغاء',
		en: 'Cancel'
	},

	go_now: {
		ar: 'اذهب الآن',
		en: 'Go now'
	},
	navigate: {
		ar: 'ملاحة',
		en: 'Navigate'
	},
	more_options: {
		ar: 'خيارات أخرى',
		en: 'More options'
	},
	late: {
		ar: 'متأخر',
		en: 'late'
	},
	call_contact: {
		ar: 'اتصال بـ',
		en: 'Call contact'
	},
	from: {
		ar: 'من',
		en: 'From'
	},

	pre_route_title: {
		ar: 'مسارك',
		en: 'Your route'
	},
	pre_route_ready: {
		ar: 'جاهز للانطلاق؟',
		en: 'Ready to go?'
	},
	pre_route_pickups: {
		ar: 'استلامات',
		en: 'pickups'
	},
	pre_route_deliveries: {
		ar: 'تسليمات',
		en: 'deliveries'
	},
	pre_route_est_time: {
		ar: 'الوقت المتوقع',
		en: 'est. time'
	},
	pre_route_est_distance: {
		ar: 'المسافة المتوقعة',
		en: 'est. distance'
	},
	pre_route_sequence: {
		ar: 'ترتيب المسار',
		en: 'Route order'
	},
	pre_route_first_stop: {
		ar: 'المحطة الأولى',
		en: 'First stop'
	},
	driving_next_stop: {
		ar: 'المحطة التالية',
		en: 'Next stop'
	},
	driving_navigate_big: {
		ar: 'الملاحة عبر خرائط جوجل',
		en: 'Navigate with Google Maps'
	},
	driving_call_buyer: {
		ar: 'اتصل بالمشتري للتأكيد',
		en: 'Call buyer to confirm'
	},
	driving_call_seller: {
		ar: 'اتصل بالبائع',
		en: 'Call seller'
	},
	driving_arriving: {
		ar: 'جاري الوصول…',
		en: 'Arriving…'
	},
	driving_im_here: {
		ar: 'وصلت',
		en: "I'm here"
	},
	driving_details: {
		ar: 'التفاصيل',
		en: 'Details'
	},
	driving_repeat_pickup: {
		ar: 'استلام متكرر',
		en: 'Repeat pickup'
	},
	location_unreliable: {
		ar: 'الموقع غير دقيق',
		en: 'Location unreliable'
	},
	location_unreliable_hint: {
		ar: 'لا نستطيع تحديد موقعك الحالي — اضغط "وصلت" عند الوصول',
		en: "Can't tell where you are — tap below when you arrive"
	},
	driving_resume: {
		ar: 'متابعة القيادة',
		en: 'Resume driving'
	},
	verify_title: {
		ar: 'تحقق من المحتويات',
		en: 'Verify contents'
	},
	verify_hint: {
		ar: 'أكد أن كل عنصر يطابق الفاتورة قبل الاستلام.',
		en: 'Confirm each item matches the receipt before picking up.'
	},
	verify_confirm_all: {
		ar: 'تأكيد الاستلام',
		en: 'Confirm pickup'
	},
	verify_all_match: {
		ar: 'تم تأكيد كل العناصر',
		en: 'All items confirmed'
	},
	verify_not_all: {
		ar: 'أكد كل العناصر للمتابعة',
		en: 'Confirm all items to continue'
	},
	verify_mismatch_note: {
		ar: 'شيء غير مطابق؟ لا تؤكد — اتصل بالعمليات وسيتم التحديث.',
		en: "Something doesn't match? Don't confirm — call ops and they will update it."
	},
	verify_expected: {
		ar: 'المتوقع',
		en: 'Expected'
	},
	verify_pieces: {
		ar: 'قطعة',
		en: 'pcs'
	},
	collect_amount_due: {
		ar: 'المبلغ المطلوب',
		en: 'Amount due'
	},
	collect_paid_online: {
		ar: 'مدفوعة مسبقاً',
		en: 'Paid online'
	},
	collect_confirm_handover: {
		ar: 'تأكيد التسليم',
		en: 'Confirm handover'
	},
	collect_different_amount: {
		ar: 'مبلغ مختلف؟',
		en: 'Different amount?'
	},
	collect_quick_exact: {
		ar: 'بالضبط',
		en: 'Exact'
	},
	collect_handover_note: {
		ar: 'لا يوجد مبلغ للتحصيل — أكد فقط أنك سلّمت الطلب.',
		en: 'No cash to collect — just confirm you handed the order over.'
	},
	noshow_title: {
		ar: 'المشتري غير متواجد؟',
		en: 'Buyer not here?'
	},
	noshow_later: {
		ar: 'العودة لاحقاً',
		en: 'Come back later'
	},
	noshow_later_hint: {
		ar: 'تنتقل إلى قائمة "لاحقاً". تابع مسارك.',
		en: 'Moves to your "Later" list. Carry on with the route.'
	},
	later_title: {
		ar: 'لاحقاً',
		en: 'Later'
	},
	later_saved: {
		ar: 'محفوظة لاحقاً',
		en: 'saved for later'
	},
	later_nearby: {
		ar: 'أنت قرب محطة محفوظة',
		en: 'You are near a saved stop'
	},
	later_tap_to_handle: {
		ar: 'اضغط للتعامل معها الآن',
		en: 'Tap to handle it now'
	},
	later_resume: {
		ar: 'استئناف المحطات المحفوظة',
		en: 'Resume saved stops'
	},
	later_go_now: {
		ar: 'اذهب الآن',
		en: 'Go now'
	},
	later_empty: {
		ar: 'لا توجد محطات محفوظة',
		en: 'No saved stops'
	},
	debrief_title: {
		ar: 'اكتمل المسار',
		en: 'Route complete'
	},
	debrief_cash_in_hand: {
		ar: 'النقد بحوزتك',
		en: 'Cash in hand'
	},
	debrief_delivered: {
		ar: 'تم التسليم',
		en: 'Delivered'
	},
	debrief_picked_up: {
		ar: 'تم الاستلام',
		en: 'Picked up'
	},
	debrief_saved_later: {
		ar: 'محفوظ لاحقاً',
		en: 'Saved for later'
	},
	debrief_resume_later: {
		ar: 'استئناف المحطات المحفوظة؟',
		en: 'Resume saved stops?'
	},
	debrief_send_invoices: {
		ar: 'إرسال الفواتير',
		en: 'Send invoices'
	},
	debrief_all_done: {
		ar: 'كل شيء تم. عد إلى نقطة البداية.',
		en: 'All done. Head back to your start point.'
	},
	cockpit_synced: {
		ar: 'متزامن',
		en: 'Synced'
	},
	cockpit_pending: {
		ar: 'قيد المزامنة',
		en: 'pending'
	},
	cockpit_in_hand: {
		ar: 'بحوزتك',
		en: 'in hand'
	},
	cockpit_all_stops: {
		ar: 'كل المحطات',
		en: 'All stops'
	},
	flash_saved: {
		ar: 'تم الحفظ',
		en: 'Saved'
	},
	flash_delivered: {
		ar: 'تم التسليم',
		en: 'Delivered'
	},
	flash_picked_up: {
		ar: 'تم الاستلام',
		en: 'Picked up'
	},
	flash_queued: {
		ar: 'ستتم المزامنة…',
		en: 'Will sync…'
	},
	overview_title: {
		ar: 'كل المحطات',
		en: 'All stops'
	},
	overview_resume_driving: {
		ar: 'متابعة القيادة',
		en: 'Resume driving'
	},
	details_items: {
		ar: 'العناصر',
		en: 'Items'
	},
	details_whatsapp: {
		ar: 'واتساب',
		en: 'WhatsApp'
	},
	details_notes: {
		ar: 'ملاحظات',
		en: 'Notes'
	},
	banner_nearby: {
		ar: 'محطة قريبة',
		en: 'Nearby stop'
	},
	banner_handle_now: {
		ar: 'تعامل الآن',
		en: 'Handle now'
	},

	active_routes: {
		ar: 'خطوط نشطة',
		en: 'active routes'
	},
	back_to_active: {
		ar: 'العودة إلى الخط الحالي',
		en: 'back to active'
	},
	dismiss: {
		ar: 'إغلاق',
		en: 'dismiss'
	},
	edit_mode: {
		ar: 'وضع التعديل',
		en: 'edit mode'
	},
	error_happened: {
		ar: 'حدث خطأ',
		en: 'an error happened'
	},
	invalid_date: {
		ar: 'تاريخ غير صالح',
		en: 'invalid date'
	},
	logout_fail: {
		ar: 'فشل تسجيل الخروج',
		en: 'logout failed'
	},
	message_whatsapp: {
		ar: 'رسالة واتساب',
		en: 'whatsapp message'
	},
	new_coordinates: {
		ar: 'إحداثيات جديدة',
		en: 'new coordinates'
	},
	new_option: {
		ar: 'خيار جديد',
		en: 'new option'
	},
	new_updates_please_reload: {
		ar: 'تحديثات جديدة، الرجاء إعادة تحميل التطبيق',
		en: 'new updates, please reload'
	},
	next_2_months: {
		ar: 'الشهرين القادمين',
		en: 'next 2 months'
	},
	next_2_weeks: {
		ar: 'الأسبوعين القادمين',
		en: 'next 2 weeks'
	},
	next_month: {
		ar: 'الشهر القادم',
		en: 'next month'
	},
	next_week: {
		ar: 'الأسبوع القادم',
		en: 'next week'
	},
	no_name: {
		ar: 'بدون اسم',
		en: 'no name'
	},
	no_routes_available: {
		ar: 'لا توجد خطوط متاحة',
		en: 'no routes available'
	},
	operator: {
		ar: 'مشغل',
		en: 'operator'
	},
	account: {
		ar: 'حساب',
		en: 'account'
	},
	periods: {
		ar: 'فترات',
		en: 'periods'
	},
	remove_window: {
		ar: 'حذف الفترة',
		en: 'remove window'
	},
	select_date: {
		ar: 'اختر التاريخ',
		en: 'select date'
	},
	signup_failed: {
		ar: 'فشل إنشاء الحساب',
		en: 'signup failed'
	},
	switch_route: {
		ar: 'تبديل الخط',
		en: 'switch route'
	},
	update_password_error: {
		ar: 'خطأ في تحديث كلمة المرور',
		en: 'update password error'
	},
	viewing_finished_route: {
		ar: 'عرض خط منتهي',
		en: 'viewing finished route'
	},
	page: {
		ar: 'صفحة',
		en: 'page'
	},
	no_matching_invoices: {
		ar: 'لا توجد فواتير مطابقة',
		en: 'no matching invoices'
	},
	no_matching_results: {
		ar: 'لا توجد نتائج مطابقة',
		en: 'no matching results'
	},
	expand_columns: {
		ar: 'إظهار الأعمدة',
		en: 'show columns'
	},
	collapse_columns: {
		ar: 'إخفاء الأعمدة',
		en: 'hide columns'
	}
} as const;
